import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';
import { EMPTY, Subscription, catchError, map } from 'rxjs';
import { MobileAlert, errorObjectToString, getBrowserInfo, sessionId } from 'src/utils/common';
import { isProduction, logArt, version } from 'src/utils/config';
import {
  observeCustomPromotionSectionFor,
  observeFriebaseAuth,
  observeStoreDoc,
  observeUserDoc,
  orderSubscriptionForStore,
  setUserForGa,
  setUserPropertiesForGa,
  submittedOrderSubscriptionForStore,
  updateUser,
} from 'src/utils/firebase/firebase-short-cut';
import { logger } from 'src/utils/logger';
import { validateStore, validateUser } from 'src/utils/validators/validate-auth';

import useAuth from 'src/redux/hooks/useAuth';
import useHomeConf from 'src/redux/hooks/useHomeConf';
import useOrder from 'src/redux/hooks/useOrder';
import { UserExceptFavoriteProducts } from 'src/redux/slices/auth';
import { selectStoreId, selectStoreNickname, selectUser } from 'src/redux/slices/auth';
import { useAppSelector } from 'src/redux/store';

let userSubscription: Subscription;
let storeSubscription: Subscription;
let submittedOrderSubscription: Subscription;
let orderHistorySubscription: Subscription;
let customPromotionSectionSubscription: Subscription;

const handleAuthError = (subscription: Subscription | undefined, logout: () => void) => (error: any) => {
  console.error(error);
  const errorMessage = errorObjectToString(error);
  logger.logCommerce(`사용자 인증 에러: ${errorMessage}`, {
    level: 'error',
  });
  logout();
  subscription?.unsubscribe();
  MobileAlert(errorMessage, null, {
    text: '새로고침',
    onClick: () => window.location.reload(),
  });
  return EMPTY;
};

const useAuthentication = () => {
  const [userForInit, setUserForInit] = useState<UserExceptFavoriteProducts | null>();
  const user = useAppSelector(selectUser);
  const storeId = useAppSelector(selectStoreId);
  const storeNickname = useAppSelector(selectStoreNickname);
  const { onSetAuthLoading, onSetUser, onSetStore, onSetFavoriteProducts, onLogout } = useAuth();
  const { onSetSubmittedOrder, onSetOrderHistory, onSetOnEditOrder } = useOrder();
  const { onSetCustomPromotionSections } = useHomeConf();

  useEffect(() => {
    const authSubscription = observeFriebaseAuth().subscribe(async (firebaseAuth) => {
      const { uid, email } = firebaseAuth ?? {};

      if (uid && email) {
        // 사용자 접속 정보 기록. 읽기(observeUserDoc)를 막지 않도록 await하지 않는다.
        updateUser(uid, {
          email,
          ip: logger.getPublicIp(),
          sessionId: sessionId(),
          userAgent: getBrowserInfo(),
          version,
        }).catch((error) => console.error('updateUser 실패:', error));
        userSubscription = observeUserDoc(uid)
          .pipe(catchError(handleAuthError(userSubscription, onLogout)))
          .subscribe((user0) => {
            try {
              validateUser(user0);
            } catch (error) {
              const errorMessage = errorObjectToString(error);
              MobileAlert(errorMessage, null, {
                text: '확인',
                onClick: () => {
                  onLogout();
                  window.location.reload();
                },
              });
              return;
            }
            const { favoriteProducts, ...user } = user0;
            onSetUser(user);
            onSetFavoriteProducts(favoriteProducts ?? []);
          });
      } else {
        userSubscription?.unsubscribe();
        storeSubscription?.unsubscribe();
        submittedOrderSubscription?.unsubscribe();
        orderHistorySubscription?.unsubscribe();
        customPromotionSectionSubscription?.unsubscribe();
        // 비회원의 최초 로딩이 해제되는 시점.
        onLogout();
      }
    });
    return () => {
      authSubscription.unsubscribe();
      userSubscription?.unsubscribe();
    };
  }, [onLogout, onSetFavoriteProducts, onSetUser]);

  useEffect(() => {
    if (storeId) {
      storeSubscription = observeStoreDoc(storeId)
        .pipe(catchError(handleAuthError(storeSubscription, onLogout)))
        .subscribe((store) => {
          validateStore(store);
          onSetStore(store);
        });

      // 진행중 주문만 보는 작고 빠른 구독 — 전역 로딩 게이트를 여기에 건다.
      submittedOrderSubscription = submittedOrderSubscriptionForStore(storeId)
        .pipe(catchError(handleAuthError(submittedOrderSubscription, onLogout)))
        .subscribe((orders) => {
          onSetSubmittedOrder(orders);
          // 11시가 지나서 수정가능하지 못하게 되는 경우 강제로 수정모드를 해제한다.
          if (orders.length === 0) {
            onSetOnEditOrder(false);
          }
          onSetAuthLoading(false);
        });

      // 6개월 주문 내역은 백그라운드로 — 전역 로딩과 무관하다.
      orderHistorySubscription = orderSubscriptionForStore(storeId)
        .pipe(catchError(handleAuthError(orderHistorySubscription, onLogout)))
        .subscribe(onSetOrderHistory);

      customPromotionSectionSubscription = observeCustomPromotionSectionFor(storeId)
        .pipe(map((docs) => docs.filter((doc) => doc.isDeleted !== true && doc.isActive === true)))
        .subscribe(onSetCustomPromotionSections);

      return () => {
        storeSubscription?.unsubscribe();
        submittedOrderSubscription?.unsubscribe();
        orderHistorySubscription?.unsubscribe();
        customPromotionSectionSubscription?.unsubscribe();
      };
    }
  }, [
    onLogout,
    onSetAuthLoading,
    onSetCustomPromotionSections,
    onSetOnEditOrder,
    onSetOrderHistory,
    onSetSubmittedOrder,
    onSetStore,
    storeId,
  ]);

  // 최초 로그인 여부 판단
  useEffect(() => {
    if (user?._id !== userForInit?._id) {
      setUserForInit(user);
    } else if (!user) {
      setUserForInit(null);
    }
  }, [user, userForInit?._id]);

  // 최초 로그인에 따른 로그
  useEffect(() => {
    if (userForInit === undefined) {
      console.log(`%c\n${logArt}`, isProduction ? 'color:orange' : 'color:black');
    }
  }, [userForInit]);

  useEffect(() => {
    // 외부 api[datadog, ga]에 사용자 정보를 업데이트합니다.
    if (userForInit && storeNickname) {
      // GA에 사용자 정보를 업데이트합니다.
      setUserForGa(userForInit._id);
      setUserPropertiesForGa(userForInit);
      datadogRum.setUser({
        id: userForInit._id,
        name: storeNickname,
        storeName: storeNickname,
        email: userForInit.email,
        deviceId: userForInit.deviceTokenId,
        localSessionId: sessionId(),
      });
    } else {
      datadogRum.setUser({
        id: 'guest-' + sessionId(),
        name: 'guest',
        storeName: storeNickname,
        email: undefined,
        deviceId: undefined,
        localSessionId: sessionId(),
      });
    }
  }, [userForInit, storeNickname]);
};

export default useAuthentication;
